/**
 * Detecção de host central (apex) x base (escola) e roteamento por subdomínio.
 *
 * Arquitetura multi-tenant:
 * - A API é ÚNICA (api-v2.escolarai.com.br/graphql). O backend resolve a BASE
 *   (banco físico) a partir do subdomínio (Origin) de onde a requisição parte.
 * - Por isso, para "entrar" numa base basta navegar para o subdomínio dela —
 *   nenhum header de tenant é necessário no frontend.
 *
 * Hosts:
 * | Ambiente | Central (apex)             | Base (escola)                       |
 * |----------|----------------------------|-------------------------------------|
 * | Prod     | escolarai.com.br           | <slug>.escolarai.com.br             |
 * | Homol    | front-v2.escolarai.com.br  | <slug>-homol.escolarai.com.br       |
 *
 * Em localhost / qualquer outro host, `getCentralEnv` retorna null e o app roda
 * normalmente (sem combo de seleção).
 */

export type CentralEnv = "prod" | "homol";

const PROD_CENTRAL_HOSTS = ["escolarai.com.br", "www.escolarai.com.br"];
const HOMOL_CENTRAL_HOSTS = ["front-v2.escolarai.com.br"];

const ROOT_DOMAIN = "escolarai.com.br";
const STORAGE_KEY = "escolarai.base.slug";
/** Query param que força reexibir o combo (usado pelo "Trocar escola"). */
export const TROCAR_PARAM = "trocar";

/** Retorna o ambiente se o host atual for uma apex central; senão null. */
export function getCentralEnv(
  host: string = window.location.hostname,
): CentralEnv | null {
  const h = host.toLowerCase();
  if (PROD_CENTRAL_HOSTS.includes(h)) return "prod";
  if (HOMOL_CENTRAL_HOSTS.includes(h)) return "homol";
  return null;
}

export function isCentralHost(host?: string): boolean {
  return getCentralEnv(host) !== null;
}

/** Monta a URL absoluta da base a partir do slug, preservando o path. */
export function buildBaseUrl(
  slug: string,
  env: CentralEnv,
  path: string = "/",
): string {
  const sub = env === "homol" ? `${slug}-homol` : slug;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `https://${sub}.${ROOT_DOMAIN}${cleanPath}`;
}

/**
 * Slug da base (tenant) atual, derivado do subdomínio do host. Retorna null em
 * host central (apex) e cai no slug persistido em localStorage como fallback
 * (localhost/dev, onde não há subdomínio de base).
 *
 * Ex.: buenosaires.escolarai.com.br → "buenosaires"
 *      buenosaires-homol.escolarai.com.br → "buenosaires"
 */
export function getCurrentBaseSlug(
  host: string = window.location.hostname,
): string | null {
  const h = host.toLowerCase();
  // Apex central não representa nenhuma base específica.
  if (getCentralEnv(h)) return null;
  // <slug>[-homol].escolarai.com.br → extrai o primeiro rótulo do subdomínio.
  if (h.endsWith(`.${ROOT_DOMAIN}`)) {
    const sub = h.slice(0, h.length - ROOT_DOMAIN.length - 1);
    const firstLabel = sub.split(".").filter(Boolean)[0] ?? "";
    const slug = firstLabel.replace(/-homol$/, "");
    if (slug) return slug;
  }
  // localhost ou host custom: usa o slug salvo ao entrar na base.
  return getSavedBaseSlug();
}

/** True quando a base (tenant) atual é a Prefeitura de Buenos Aires. */
export function isBuenosAiresBase(host?: string): boolean {
  return getCurrentBaseSlug(host) === "buenosaires";
}

export function getSavedBaseSlug(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function saveBaseSlug(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    /* localStorage indisponível (modo privado): segue sem persistir */
  }
}

export function clearSavedBaseSlug(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Redireciona a apex central para a base escolhida, preservando o path/search
 * atual (exceto o param de "trocar"). Usa replace para não poluir o histórico.
 */
export function redirectToBase(slug: string, env: CentralEnv): void {
  const params = new URLSearchParams(window.location.search);
  params.delete(TROCAR_PARAM);
  const qs = params.toString();
  const path = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
  window.location.replace(buildBaseUrl(slug, env, path));
}

/**
 * URL da apex central correspondente ao host atual, já com `?trocar=1`.
 * Use num botão "Trocar escola" dentro do app (rodando numa base).
 */
export function getTrocarBaseUrl(host: string = window.location.hostname): string {
  const h = host.toLowerCase();
  const isHomol = h.includes("-homol") || HOMOL_CENTRAL_HOSTS.includes(h);
  const central = isHomol ? HOMOL_CENTRAL_HOSTS[0] : PROD_CENTRAL_HOSTS[0];
  return `https://${central}/?${TROCAR_PARAM}=1`;
}
